import type { ApiError } from '@ctcells/api'
import type { Sample } from '@ctcells/contract'

import { FLOW_STAGES } from '../monitoring/sampleFlowModel.ts'

/**
 * **시료 처분**(중단 뒤 · `review_reason=sample_disposition`) — 정본 07 §4.4 [A-3] B-0252 · 06 SM-BATCH-06 ⊕ · 03 SVC-SCH-11 ④.
 *
 * 계약 `POST /batches/{id}/recover` `RecoverDecision`(실서버 0.28.15 원문):
 * | 결정 | 받는 시료 | 일어나는 일 |
 * | --- | --- | --- |
 * | `discard_samples` | 그 배치 · 종착 아닌(`in_process` · `awaiting_decision`) 시료 | 폐기 · 배치는 종착에 그대로 남는다 |
 * | `rerun_samples` | 위 ∧ 단계가 첫 단계(`process.stages.first`) ∧ 그 배치 분주 기록 0 | 그 시료의 `batch_id` · `assigned_well` 을 비우고 단계를 첫 단계로 — 새 작업에 다시 담는다(옛 이력은 남는다) |
 *
 * 🔴 사유(`reason`)는 둘 다 **필수**다(감사 · 폐기는 `sample.discard_reason`). 한 요청 = 한 결정이라 섞으면 요청이 둘이다 —
 *    코어 관리자 페이지 `recover_form.js` `dispositionPanel` · `sendDispositions` 와 같은 길(사유 하나를 두 요청이 같이 싣는다).
 * 🔴 받지 못한 시료는 422 `REQUEST_VALIDATION_FAIL` 의 `context.sample_ids` 로 돌아오고 **그 요청은 아무것도 바꾸지 않는다** —
 *    화면은 그 줄에 사유를 달고, 다른 결정 요청이 먼저 성공했으면 그 줄들은 «처리됨»으로 접는다.
 * ⚠ 분주 기록 조건은 화면이 모른다 — 첫 단계가 아닌 시료만 미리 [다시 돌림]을 비활성으로 둔다(관리자 페이지 `rerunOff` 와 같다).
 */
export type DispositionChoice = 'rerun_samples' | 'discard_samples'

/** 결정 차례 — 보낼 때 이 차례로 보낸다(관리자 페이지 `PICK_DECISIONS` 와 같다) */
export const DISPOSITION_DECISIONS: readonly DispositionChoice[] = [
  'discard_samples',
  'rerun_samples',
]

/** 처분 후보가 되는 시료 상태 — 서버 `batches.UNSETTLED_SAMPLE` 의 사본(계약 `sample_ids` 설명) */
export const UNSETTLED_SAMPLE_STATUSES: readonly Sample['status'][] = [
  'in_process',
  'awaiting_decision',
]

/** 시료 단계의 첫 단계 — 단계 차례 표의 처음(`FLOW_STAGES` · 전개 규칙 `process.stages.first` = registered) */
export const FIRST_SAMPLE_STAGE = FLOW_STAGES[0]

export interface DispositionCandidate {
  sampleId: string
  well: string | undefined
  stage: string
  status: Sample['status']
}

/** 그 배치의 처분 후보 — 종착 아닌 시료만 · 웰 차례(웰 없는 시료는 뒤 · 숨기지 않는다) */
export function dispositionCandidates(
  samples: readonly Pick<Sample, 'sample_id' | 'assigned_well' | 'stage' | 'status'>[],
): DispositionCandidate[] {
  return samples
    .filter((sample) => UNSETTLED_SAMPLE_STATUSES.includes(sample.status))
    .map((sample) => ({
      sampleId: sample.sample_id,
      well: sample.assigned_well ?? undefined,
      stage: sample.stage,
      status: sample.status,
    }))
    .toSorted((a, b) => {
      if (a.well === b.well) return a.sampleId.localeCompare(b.sampleId)
      if (a.well === undefined) return 1
      if (b.well === undefined) return -1
      return wellOrder(a.well, b.well)
    })
}

/** 웰 이름 차례 — 줄 글자 다음 열 번호(`A2` < `A10`) */
function wellOrder(a: string, b: string): number {
  const pa = /^([A-Za-z]*)(\d*)(.*)$/.exec(a)
  const pb = /^([A-Za-z]*)(\d*)(.*)$/.exec(b)
  const ra = pa?.[1] ?? ''
  const rb = pb?.[1] ?? ''
  if (ra !== rb) return ra < rb ? -1 : 1
  const na = pa?.[2] === undefined || pa[2] === '' ? -1 : Number(pa[2])
  const nb = pb?.[2] === undefined || pb[2] === '' ? -1 : Number(pb[2])
  if (na !== nb) return na - nb
  return (pa?.[3] ?? '').localeCompare(pb?.[3] ?? '')
}

/** [다시 돌림]을 미리 막을 시료인가 — 처리가 시작된(첫 단계가 아닌) 시료는 서버가 폐기만 받는다 */
export function rerunBlocked(candidate: Pick<DispositionCandidate, 'stage'>): boolean {
  return candidate.stage !== FIRST_SAMPLE_STAGE
}

/**
 * 고름 → 결정마다 보낼 시료. 🔴 차례는 **후보 차례(웰 차례)** 그대로다 — 사람이 누른 차례가 요청에 섞이지 않는다(계약 `sample_ids` 는
 * 집합이라 서버는 차례를 따지지 않는다 · 같은 고름이면 늘 같은 본문이 나가게 한다). 🔴 하나라도 안 골랐으면 `null` — 다 골라야 보낸다(정본 07 §4.4 [A-3]).
 * 후보가 0건이면 `null`(보낼 것이 없다).
 */
export function dispositionRequests(
  candidates: readonly Pick<DispositionCandidate, 'sampleId'>[],
  choices: ReadonlyMap<string, DispositionChoice>,
): Record<DispositionChoice, string[]> | null {
  if (candidates.length === 0) return null
  const out: Record<DispositionChoice, string[]> = { discard_samples: [], rerun_samples: [] }
  for (const candidate of candidates) {
    const choice = choices.get(candidate.sampleId)
    if (choice === undefined) return null
    out[choice].push(candidate.sampleId)
  }
  return out
}

/** 서버가 받지 않은 시료 — 422 `context.sample_ids` 그대로(없으면 빈 배열) */
export function refusedSampleIds(error: Pick<ApiError, 'context'> | null | undefined): string[] {
  const ids = error?.context?.['sample_ids']
  return Array.isArray(ids) ? ids.filter((id): id is string => typeof id === 'string') : []
}

/** 처분 한 번의 결과 — 처리된 시료 · 받지 않은 시료 · 실패 문구(결정마다 하나) */
export interface DispositionOutcome {
  done: string[]
  refused: string[]
  errors: string[]
}

/** 작업 띠 안내 → 작업 상세 + 처분 창 열림 — 검색 매개변수 하나(`?disposition=open`) */
export const DISPOSITION_PARAM = 'disposition'
export const DISPOSITION_OPEN = 'open'
export function dispositionHref(batchesPath: string, batchId: string): string {
  return `${batchesPath}/${batchId}?${DISPOSITION_PARAM}=${DISPOSITION_OPEN}`
}
